import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useKeyedPendingWrite } from '@/lib/use-pending-write';
import { isStale, problemCode, problemMessage, standingChanged } from '../api/problem';
import { useModuleText } from './module-text';
import { writeModule, type ModuleState } from './use-modules';

/**
 * «Attiva qui» and «Disattiva qui» (FR-AD-7). Neither asks first (FR-UX-5: no dialog before any
 * act): activating says so in a toast; deactivating, which revokes an entitlement, shows the
 * administration's confirmation toast without undo. A refusal says why in plain words, and a
 * stale one reads the place or the list again.
 */
export function useModuleActs({
  placeKind,
  placeId,
  placeName,
  onState,
  onChanged,
  onStanding,
}: {
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly placeName: string;
  readonly onState: (next: ModuleState) => void;
  readonly onChanged: () => void;
  readonly onStanding: () => void;
}) {
  const { t } = useTranslation('administration');
  const { name } = useModuleText();
  const { isPending, run } = useKeyedPendingWrite();

  const act = useCallback(
    (kind: 'activation' | 'deactivation', state: ModuleState) =>
      run(`${kind}:${state.module.id}`, async () => {
        const { data, error, response } = await writeModule(
          kind,
          placeKind,
          placeId,
          state.module.id,
        );
        if (data === undefined || !response.ok) {
          toast.error(problemMessage(t, error, response.status));
          // The caller's standing changed, or the place is no longer theirs: read the place again.
          if (standingChanged(error) || problemCode(error) === 'PLACE_NOT_ADMINISTERED') {
            onStanding();
          }
          if (isStale(error) || response.status === 409) {
            onChanged();
          }
          return;
        }
        onState(data);
        onChanged();
        const values = { module: name(state.module), place: placeName };
        if (kind === 'activation') {
          toast.success(t('moduleSection.activated', values));
        } else {
          toast.success(t('moduleSection.deactivated', values));
        }
      }),
    [run, placeKind, placeId, placeName, t, name, onState, onChanged, onStanding],
  );

  return {
    activate: (state: ModuleState) => act('activation', state),
    deactivate: (state: ModuleState) => act('deactivation', state),
    isPending: (state: ModuleState) =>
      isPending(`activation:${state.module.id}`) || isPending(`deactivation:${state.module.id}`),
  };
}
